import React, { useState } from 'react';
import OneLineTextField from '../../../../components/raExtend/fields/OneLineTextField';
import { useTranslate } from 'ra-core';
import { MousePointerClick } from 'lucide-react';
import { RadarPing } from '../../../../components/motion/RadarPing';
import { Switch } from '../../../../components/ui/switch';
import DefaultAlert from '../../../../components/ui/DefaultAlert';
import { TTrackingLinks } from '../../types';

type Props = {
  trackingLinks: TTrackingLinks;
};

// Publisher passback macro. The ad server captures any query param prefixed
// with `p_` as passback data and returns it in postbacks. `[CLICK_ID]` is a
// placeholder the publisher replaces with their own click identifier macro.
const CLICK_ID_MACRO = 'p_click_id=[CLICK_ID]';

// Optional source-URL macro. The ad server reads the `referrer` query param and,
// when the campaign has a source-website param configured, forwards it to the
// brand's redeem URL. `[SOURCE_URL]` is a placeholder the publisher replaces
// with their own page-URL macro.
const REFERRER_MACRO = 'referrer=[SOURCE_URL]';

const appendMacros = (clickUrl?: string, includeReferrer = false): string => {
  if (!clickUrl) return '';
  const macros = [CLICK_ID_MACRO];
  if (includeReferrer) macros.push(REFERRER_MACRO);
  return macros.reduce((url, macro) => {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${macro}`;
  }, clickUrl);
};

function TrackingLinks({ trackingLinks }: Props) {
  const t = useTranslate();
  const [includeReferrer, setIncludeReferrer] = useState(false);

  const links = {
    ...trackingLinks,
    click: appendMacros(trackingLinks?.click, includeReferrer),
    pixel: `<img src="${trackingLinks?.pixel}" alt="" border="0" height="1" width="1" style="display:none;" />`,
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Click tracker — primary / most important */}
      <div className="relative rounded-xl border-2 border-violet/40 bg-card p-4 flex flex-col gap-3 overflow-hidden shadow-[0_0_18px_-4px_var(--gl-violet)]">
        {/* Corner glow */}
        <div
          aria-hidden
          className="pointer-events-none absolute -right-8 -top-8 size-40 rounded-full bg-violet opacity-20 blur-3xl"
        />
        <div className="relative flex items-center gap-2">
          <div className="relative size-7 rounded-md bg-violet flex items-center justify-center shrink-0">
            <RadarPing className="bg-violet" />
            <MousePointerClick className="relative size-3.5 text-white" />
          </div>
          <p className="text-sm font-semibold">Click tracker</p>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed -mt-1">
          This is the most important link — use it wherever you want users to
          click in your content.
        </p>
        <OneLineTextField
          label={t('common.text.clicks')}
          record={links}
          source="click"
          helperText={t('offer.drawer.tracking.click_macro_hint')}
          highlight
        />

        {/* Optional: append the source-URL (referrer) macro */}
        <div className="relative flex items-center gap-2">
          <Switch
            id="tracking-referrer-toggle"
            checked={includeReferrer}
            onCheckedChange={setIncludeReferrer}
          />
          <label
            htmlFor="tracking-referrer-toggle"
            className="text-xs font-medium cursor-pointer select-none"
          >
            {t('offer.drawer.tracking.referrer_toggle_label')}
          </label>
        </div>
        {includeReferrer && (
          <DefaultAlert
            severity="info"
            className="relative"
            alertDescription={t('offer.drawer.tracking.referrer_hint')}
          />
        )}
      </div>

      {/* Secondary trackers */}
      <div className="flex flex-col">
        <OneLineTextField
          label={t('common.text.impressions')}
          record={links}
          source="impression"
        />
        <OneLineTextField
          label={t('common.text.pixel')}
          record={links}
          source="pixel"
        />
      </div>
    </div>
  );
}

export default TrackingLinks;
